import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { putawayService } from '../../services/api';
import toast from './toast';
import {
  parsePutawayBulkExcelPreview,
  parsePutawayBulkExcelStructure,
  groupPreviewRowsByPutaway,
} from '../../utils/parsePutawayBulkExcelPreview';

function normalizeBulkFailures(data) {
  const failures = data?.failures;
  if (!Array.isArray(failures)) return [];
  return failures.map((f) => ({
    row: f.row ?? f.rowIndex ?? '—',
    putawayId: f.putawayId ?? '',
    field: f.field ?? '',
    error: f.error ?? f.message ?? 'Validation error',
  }));
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const BulkAssignBinPopUp = ({
  open = false,
  onClose,
  selectedPutaways = [],
  onSuccess,
}) => {
  const fileInputRef = useRef(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadSummary, setDownloadSummary] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [clientErrors, setClientErrors] = useState([]);
  const [backendFailures, setBackendFailures] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [step, setStep] = useState('start'); // start | preview | error

  const selectedPutawayIds = useMemo(
    () => (selectedPutaways || []).map((p) => p.putawayId).filter(Boolean),
    [selectedPutaways]
  );

  const groupedPreview = useMemo(() => groupPreviewRowsByPutaway(previewRows), [previewRows]);

  const resetState = () => {
    setSelectedFile(null);
    setPreviewRows([]);
    setClientErrors([]);
    setBackendFailures([]);
    setAssigning(false);
    setDownloadSummary(null);
    setStep('start');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !assigning) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, assigning, onClose]);

  const downloadPrefilledExcel = useCallback(async () => {
    if (!selectedPutawayIds.length) {
      toast.error('Select at least one putaway.');
      return false;
    }

    setDownloadLoading(true);
    setBackendFailures([]);
    try {
      const { blob, filename } = await putawayService.downloadBulkAssignBinsTemplate(selectedPutawayIds);
      const buffer = await blob.arrayBuffer();
      const { rows, errors, putawayCount, slotCount } = parsePutawayBulkExcelStructure(
        buffer,
        selectedPutawayIds
      );

      if (errors.length || !rows.length) {
        const message =
          errors[0]?.error ||
          'Downloaded worksheet has no pre-populated allocation rows for the selected putaways.';
        setBackendFailures(errors.length ? errors : [{ row: '—', error: message }]);
        setStep('error');
        toast.error(message);
        return false;
      }

      triggerBlobDownload(blob, filename);
      setDownloadSummary({
        putawayCount,
        slotCount,
        filename,
      });
      toast.success(
        `Pre-populated worksheet downloaded (${slotCount} slot${slotCount === 1 ? '' : 's'} for ${putawayCount} putaway${putawayCount === 1 ? '' : 's'}). Fill Condition and Bin Code only.`
      );
      return true;
    } catch (err) {
      const detailMessages = Array.isArray(err?.details)
        ? err.details.map((d) =>
            d.putawayId ? `${d.putawayId}: ${d.error || 'Unable to generate slots'}` : d.error
          )
        : [];
      const failures = [
        { row: '—', error: err?.message || 'Failed to download allocation worksheet' },
        ...detailMessages.map((message) => ({ row: '—', error: message })),
      ];
      setBackendFailures(failures);
      setStep('error');
      toast.error(err?.message || 'Failed to download allocation worksheet');
      return false;
    } finally {
      setDownloadLoading(false);
    }
  }, [selectedPutawayIds]);

  const handleDownloadExcel = () => downloadPrefilledExcel();

  const handleChooseFile = () => fileInputRef.current?.click();

  const handleFileSelect = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;

    const name = (file.name || '').toLowerCase();
    if (!name.endsWith('.xlsx')) {
      setClientErrors([{ row: 0, error: 'Please upload a valid Excel (.xlsx) file.' }]);
      setStep('error');
      e.target.value = '';
      return;
    }

    setSelectedFile(file);
    setBackendFailures([]);
    setAssigning(false);

    try {
      const buffer = await file.arrayBuffer();
      const { rows, errors } = parsePutawayBulkExcelPreview(buffer);

      const extraErrors = [];
      const selectedIdSet = new Set(selectedPutawayIds.map((id) => String(id)));
      const uploadedPutawayIds = new Set(rows.map((r) => r.putawayId));

      for (const id of selectedIdSet) {
        if (!uploadedPutawayIds.has(id)) {
          extraErrors.push({
            putawayId: id,
            error: `Selected Putaway ${id} is missing from the uploaded file`,
          });
        }
      }

      const blockingErrors = [...(errors || []), ...extraErrors];
      setClientErrors(blockingErrors);
      setPreviewRows(rows);

      if (!rows.length) {
        setStep('error');
        return;
      }

      setStep(blockingErrors.length ? 'error' : 'preview');
    } catch (err) {
      setClientErrors([{ row: 0, error: err?.message || 'Failed to read Excel file' }]);
      setStep('error');
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewRows([]);
    setClientErrors([]);
    setBackendFailures([]);
    setStep('start');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleConfirmAssign = async () => {
    if (!selectedFile || assigning) return;
    if (clientErrors.length > 0) return;

    setAssigning(true);
    setBackendFailures([]);
    try {
      const res = await putawayService.bulkAssignPutawayBins(selectedFile);
      onSuccess?.(res);
      onClose?.();
    } catch (err) {
      const data = err?.response?.data;
      const failures = normalizeBulkFailures(data);
      if (failures.length) {
        setBackendFailures(failures);
      } else {
        setBackendFailures([
          { row: '—', error: err?.message || data?.error || 'Bulk bin allocation failed' },
        ]);
      }
      setStep('error');
    } finally {
      setAssigning(false);
    }
  };

  if (!open) return null;

  const canConfirm = step === 'preview' && previewRows.length > 0 && clientErrors.length === 0 && selectedFile;
  const showPreviewTable = previewRows.length > 0 && (step === 'preview' || step === 'error');

  const popup = (
    <div
      className="fixed inset-0 flex items-center justify-center p-3"
      style={{ zIndex: 99999 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-assign-bin-title"
    >
      <div className="absolute inset-0 bg-black/50" onClick={() => !assigning && onClose?.()} aria-hidden="true" />
      <div
        className="relative w-full max-w-5xl max-h-[92vh] flex flex-col bg-white rounded-2xl shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-100">
          <div>
            <h3 id="bulk-assign-bin-title" className="text-lg font-semibold text-gray-900">
              Bulk Assign Bin
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {selectedPutaways.length} putaway{selectedPutaways.length === 1 ? '' : 's'} selected — slot quantities are calculated by the system
            </p>
          </div>
          <button
            type="button"
            onClick={() => !assigning && onClose?.()}
            disabled={assigning}
            className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          <section className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
            <p className="text-sm font-semibold text-emerald-900 mb-2">How it works</p>
            <ol className="text-sm text-emerald-900/90 list-decimal list-inside space-y-1">
              <li>Click <strong>Download Excel</strong> to get the pre-populated worksheet for selected putaways.</li>
              <li>System columns (Putaway ID, SKU, quantities, slots) are already filled — do not change them.</li>
              <li>Enter only <strong>Condition</strong> (GOOD or HOLD) and <strong>Bin Code</strong> for each row.</li>
              <li>Upload the completed file, review the preview, then confirm assignment.</li>
            </ol>
          </section>

          {downloadSummary && (
            <section className="rounded-xl border border-blue-100 bg-blue-50/70 p-4 text-sm text-blue-900">
              <p className="font-semibold">Worksheet ready</p>
              <p className="mt-1">
                {downloadSummary.slotCount} allocation row{downloadSummary.slotCount === 1 ? '' : 's'} for{' '}
                {downloadSummary.putawayCount} putaway{downloadSummary.putawayCount === 1 ? '' : 's'} in{' '}
                <span className="font-medium">{downloadSummary.filename}</span>
              </p>
            </section>
          )}

          <section>
            <p className="text-sm font-semibold text-gray-800 mb-2">Selected putaways</p>
            <div className="flex flex-wrap gap-2">
              {selectedPutaways.map((p) => (
                <span
                  key={p.putawayId || p._id}
                  className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700"
                >
                  {p.putawayId}
                  {p.product?.sku ? ` · ${p.product.sku}` : ''}
                </span>
              ))}
            </div>
          </section>

          <section className="flex flex-wrap gap-3 items-center">
            <button
              type="button"
              onClick={handleDownloadExcel}
              disabled={downloadLoading || !selectedPutawayIds.length}
              className="h-10 rounded-full border border-[#188742] bg-[#188742] px-5 text-sm font-semibold text-white hover:bg-[#15743a] disabled:opacity-50"
            >
              {downloadLoading ? 'Downloading...' : 'Download Excel'}
            </button>
            <button
              type="button"
              onClick={handleChooseFile}
              disabled={assigning}
              className="h-10 rounded-full border border-gray-300 bg-white px-5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {selectedFile ? 'Choose Different File' : 'Upload Completed Excel'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={handleFileSelect}
            />
            {selectedFile && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="truncate max-w-[240px]">{selectedFile.name}</span>
                <button type="button" onClick={handleRemoveFile} className="text-red-600 hover:underline text-xs">
                  Remove
                </button>
              </div>
            )}
          </section>

          {(clientErrors.length > 0 || backendFailures.length > 0) && (
            <section className="rounded-xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-semibold text-red-800 mb-2">Validation errors</p>
              <ul className="space-y-2 max-h-40 overflow-y-auto text-sm text-red-700">
                {[...clientErrors, ...backendFailures].map((err, idx) => (
                  <li key={`${err.row}-${idx}`} className="border-b border-red-100 pb-2 last:border-0">
                    {err.row ? <span className="font-medium">Row {err.row}</span> : null}
                    {err.putawayId ? <span> · Putaway {err.putawayId}</span> : null}
                    {err.field ? <span> · {err.field}</span> : null}
                    <div>{typeof err === 'string' ? err : err.error}</div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {showPreviewTable && (
            <section>
              <p className="text-sm font-semibold text-gray-800 mb-2">Upload preview (system quantities are read-only)</p>
              <div className="space-y-4">
                {Array.from(groupedPreview.entries()).map(([putawayId, slots]) => {
                  const first = slots[0] || {};
                  return (
                    <div key={putawayId} className="rounded-xl border border-gray-200 overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 text-sm">
                        <span className="font-semibold">{putawayId}</span>
                        {first.productSku ? <span className="text-gray-600"> · {first.productSku}</span> : null}
                        {first.totalQuantity !== '' && first.totalQuantity != null ? (
                          <span className="text-gray-600"> · Total: {first.totalQuantity}</span>
                        ) : null}
                        <span className="text-gray-600"> · Slots: {slots.length}</span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-100 bg-white">
                              <th className="text-left py-2 px-3 font-semibold text-gray-600">Slot</th>
                              <th className="text-right py-2 px-3 font-semibold text-gray-600">Qty (system)</th>
                              <th className="text-left py-2 px-3 font-semibold text-gray-600">Bin Code</th>
                              <th className="text-left py-2 px-3 font-semibold text-gray-600">Condition</th>
                              <th className="text-left py-2 px-3 font-semibold text-gray-600">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {slots.map((slot, i) => (
                              <tr key={`${putawayId}-${i}`} className="border-b border-gray-50 last:border-0">
                                <td className="py-2 px-3">{slot.slotNo || i + 1}</td>
                                <td className="py-2 px-3 text-right font-medium text-gray-900 bg-gray-50/80">
                                  {slot.slotQuantity ?? '—'}
                                </td>
                                <td className="py-2 px-3">{slot.binCode || '—'}</td>
                                <td className="py-2 px-3">{slot.condition || '—'}</td>
                                <td className="py-2 px-3">
                                  <span
                                    className={
                                      slot.clientStatus === 'ready'
                                        ? 'text-emerald-700'
                                        : slot.clientStatus === 'pending'
                                          ? 'text-amber-700'
                                          : 'text-red-600'
                                    }
                                  >
                                    {slot.clientStatus === 'ready'
                                      ? 'Ready'
                                      : slot.clientStatus === 'pending'
                                        ? 'Missing input'
                                        : 'Invalid'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-white">
          <button
            type="button"
            onClick={() => !assigning && onClose?.()}
            disabled={assigning}
            className="h-10 rounded-full border border-gray-300 bg-white px-5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmAssign}
            disabled={!canConfirm || assigning}
            className="h-10 rounded-full border border-emerald-500 bg-emerald-500 px-5 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {assigning ? 'Assigning...' : 'Confirm & Assign'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(popup, document.body);
};

export default BulkAssignBinPopUp;
